import React, { useState, useMemo } from 'react';

interface FormulaDoc {
  name: string;
  syntax: string;
  description: string;
  category: 'Math' | 'Logical' | 'Text' | 'Lookup' | 'Statistical';
}

const FORMULA_REGISTRY: FormulaDoc[] = [
  // Logical
  { name: 'IF', syntax: 'IF(logical_test, value_if_true, value_if_false)', description: 'Returns one value if a logical expression is true and another if it is false.', category: 'Logical' },
  { name: 'AND', syntax: 'AND(logical1, [logical2, ...])', description: 'Returns TRUE if all of the provided arguments are logically true, and FALSE if any of the provided arguments are logically false.', category: 'Logical' },
  { name: 'OR', syntax: 'OR(logical1, [logical2, ...])', description: 'Returns TRUE if any of the provided arguments are logically true, and FALSE if all of the provided arguments are logically false.', category: 'Logical' },
  { name: 'NOT', syntax: 'NOT(logical_expression)', description: 'Returns the opposite of a logical value - NOT(TRUE) returns FALSE; NOT(FALSE) returns TRUE.', category: 'Logical' },
  { name: 'IFERROR', syntax: 'IFERROR(value, value_if_error)', description: 'Returns the first argument if it is not an error value, otherwise returns the second argument.', category: 'Logical' },
  
  // Text
  { name: 'CONCAT', syntax: 'CONCAT(value1, [value2, ...])', description: 'Concatenates the string representations of all passed arguments.', category: 'Text' },
  { name: 'CONCATENATE', syntax: 'CONCATENATE(value1, [value2, ...])', description: 'Appends strings to one another.', category: 'Text' },
  { name: 'UPPER', syntax: 'UPPER(text)', description: 'Converts a specified string to uppercase.', category: 'Text' },
  { name: 'LOWER', syntax: 'LOWER(text)', description: 'Converts a specified string to lowercase.', category: 'Text' },
  { name: 'LEN', syntax: 'LEN(text)', description: 'Returns the length of a string.', category: 'Text' },

  // Math & Statistical
  { name: 'SUM', syntax: 'SUM(value1, [value2, ...])', description: 'Returns the sum of a series of numbers and/or cells.', category: 'Math' },
  { name: 'AVERAGE', syntax: 'AVERAGE(value1, [value2, ...])', description: 'Returns the numerical average value in a dataset, ignoring text and empty cells.', category: 'Statistical' },
  { name: 'COUNT', syntax: 'COUNT(value1, [value2, ...])', description: 'Returns the number of numeric values in a dataset.', category: 'Statistical' },
  { name: 'COUNTA', syntax: 'COUNTA(value1, [value2, ...])', description: 'Returns the number of values in a dataset.', category: 'Statistical' },
  { name: 'MAX', syntax: 'MAX(value1, [value2, ...])', description: 'Returns the maximum value in a numeric dataset.', category: 'Statistical' },
  { name: 'MIN', syntax: 'MIN(value1, [value2, ...])', description: 'Returns the minimum value in a numeric dataset.', category: 'Statistical' },
  { name: 'COUNTIF', syntax: 'COUNTIF(range, criterion)', description: 'Returns a conditional count across a range.', category: 'Statistical' },
  { name: 'SUMIF', syntax: 'SUMIF(range, criterion, [sum_range])', description: 'Returns a conditional sum across a range.', category: 'Math' },

  // Lookup
  { name: 'VLOOKUP', syntax: 'VLOOKUP(search_key, range, index)', description: 'Vertical lookup. Searches down the first column of a range for a key and returns the value of a specified cell in the row found.', category: 'Lookup' },
  { name: 'HLOOKUP', syntax: 'HLOOKUP(search_key, range, index)', description: 'Horizontal lookup. Searches across the first row of a range for a key and returns the value of a specified cell in the column found.', category: 'Lookup' },
  { name: 'XLOOKUP', syntax: 'XLOOKUP(search_key, lookup_range, return_range, [missing_value], [match_mode])', description: 'Searches a range for a match and returns the corresponding item from a second range.', category: 'Lookup' },
];

interface FormulaListModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FormulaListModal: React.FC<FormulaListModalProps> = ({ isOpen, onClose }) => {
  const [search, setSearch] = useState('');

  const filteredFormulas = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    return FORMULA_REGISTRY.filter(f => 
      f.name.toLowerCase().includes(lowerSearch) || 
      f.description.toLowerCase().includes(lowerSearch)
    ).sort((a, b) => a.name.localeCompare(b.name));
  }, [search]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
      <div className="bg-white rounded-lg shadow-2xl border border-gray-300 w-full max-w-2xl max-h-[85vh] flex flex-col pointer-events-auto">
        <div className="flex justify-between items-center p-5 border-b border-gray-200 shrink-0">
          <h2 className="text-xl font-bold text-gray-800">Formula Dictionary</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        
        <div className="p-4 shrink-0 border-b border-gray-100 bg-gray-50">
          <input
            type="text"
            autoFocus
            placeholder="Search formulas... (e.g., VLOOKUP, SUM)"
            className="w-full p-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none text-sm shadow-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex-1 overflow-y-auto p-2 bg-gray-50 custom-scrollbar">
          {filteredFormulas.length === 0 ? (
             <div className="text-center py-10 text-gray-500">No formulas found matching "{search}".</div>
          ) : (
            <div className="space-y-3 p-2">
              {filteredFormulas.map((doc) => (
                <div key={doc.name} className="bg-white border border-gray-200 rounded shadow-sm p-4 hover:border-blue-300 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-blue-700 text-lg">{doc.name}</h3>
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider bg-gray-100 px-2 py-1 rounded">
                      {doc.category}
                    </span>
                  </div>
                  <div className="bg-gray-800 text-green-400 font-mono text-xs p-2 rounded mb-2 overflow-x-auto whitespace-nowrap">
                    {doc.syntax}
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {doc.description}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};